"use client";

import { storage } from "@/lib/storage";
import { SellerUnitAndProject } from "@/types/sup";
import { logger } from "@/utils/logger";
import { useEffect } from "react";
import { useBuyerStore } from "../stores/buyer/buyer-store";
import { useComparisonStore } from "../stores/comparison-store";

/**
 * StoreHydrator — hydrates singleton stores from IndexedDB on mount.
 *
 * All four Group A stores (user, buyer, seller, comparison) are global
 * singletons created via `zustand/create`. This component runs a one-time
 * client-side effect to restore persisted data (favorites, comparison tray)
 * from IndexedDB into the already-existing store instances.
 *
 * Mount this once in the app tree as a sibling (e.g. in layout.tsx).
 * It intentionally does NOT wrap children in any Context providers.
 */
export function UserHydrator() {
	// Hydrate favorites + comparison tray from IndexedDB on mount
	useEffect(() => {
		if (typeof window === "undefined") return;

		const initializeFromStorage = async () => {
			try {
				// Initialize favorites from IndexedDB
				const storedFavorites = await storage.getItem<SellerUnitAndProject[]>("tray_properties");
				if (storedFavorites) {
					useBuyerStore.getState().initFavorites(storedFavorites);
				}

				// Initialize comparison tray from IndexedDB
				const storedComparisonTray = await storage.getItem<SellerUnitAndProject[]>("comparison_tray");

				if (storedComparisonTray && Array.isArray(storedComparisonTray)) {
					useComparisonStore.getState().initComparisonTray(storedComparisonTray);
				} else if (storedComparisonTray !== null && storedComparisonTray !== undefined) {
					// Log warning only if data exists but is invalid format (security concern)
					logger.warn("Invalid comparison tray data format in storage");
				}
			} catch (error) {
				logger.error("Error loading data from storage:", error);
			}
		};

		// Use microtask to avoid blocking initial render
		Promise.resolve().then(initializeFromStorage);
	}, []);

	return null;
}
